import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  /**
   * Dark, desaturated base map (the OSM tiles through a CSS filter, see
   * RedundantTileLayers) instead of the coloured OSM look. Browser-local
   * preference, default off — the coloured map stays the default in both
   * colour schemes.
   */
  darkMapTiles: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setDarkMapTiles: (enabled: boolean) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      // Light is the default on every device; dark only by an explicit toggle (persisted)
      theme: 'light',
      darkMapTiles: false,
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'light' ? 'dark' : 'light',
        })),
      setTheme: (theme) => set({ theme }),
      setDarkMapTiles: (enabled) => set({ darkMapTiles: enabled }),
    }),
    {
      name: 'gridpilot-theme', // LocalStorage key
      // v0 stored the old dark default (also written by every settings save), so it
      // is no user choice: reset it once to light, keep the map-tile preference
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<ThemeState>;
        return (version < 1 ? { ...state, theme: 'light' } : state) as ThemeState;
      },
    }
  )
);
